import { formatCNPJ, formatCPF } from '@westhetics/ui'

/**
 * CPF e CNPJ no formulário: normalização, dígito verificador e máscara (RT-05, RT-17, RN-ADM-01).
 * A regra é a mesma da API (`_shared/utils/doc-number.ts`): o CNPJ aceita a raiz alfanumérica, com
 * os 12 primeiros caracteres em letras ou dígitos e os 2 verificadores sempre dígitos. Quem decide
 * é sempre a API; aqui a conferência só adianta o erro para a saída do campo.
 */

export type DocType = 'cnpj' | 'cpf'

export const DOC_TYPE_LABELS: Record<DocType, string> = { cnpj: 'CNPJ', cpf: 'CPF' }

export function onlyDigits(text: string) {
  return text.replace(/\D/g, '')
}

/** Documento sem máscara, com as letras do CNPJ em maiúsculas. */
export function normalizeDocNumber(text: string) {
  return text
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 14)
}

function allSame(digits: string) {
  return /^(\d)\1+$/.test(digits)
}

export function isValidCPF(text: string): boolean {
  const digits = onlyDigits(text)
  if (digits.length !== 11 || allSame(digits)) return false
  const check = (length: number) => {
    let sum = 0
    for (let i = 0; i < length; i++) sum += Number(digits[i]) * (length + 1 - i)
    const rest = (sum * 10) % 11
    return rest === 10 ? 0 : rest
  }
  return check(9) === Number(digits[9]) && check(10) === Number(digits[10])
}

export function isValidCNPJ(text: string): boolean {
  const doc = normalizeDocNumber(text)
  if (!/^[0-9A-Z]{12}\d{2}$/.test(doc) || allSame(doc)) return false
  const check = (length: number) => {
    const weights =
      length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    let sum = 0
    for (let i = 0; i < length; i++) sum += (doc.charCodeAt(i) - 48) * (weights[i] ?? 0)
    const rest = sum % 11
    return rest < 2 ? 0 : 11 - rest
  }
  return check(12) === Number(doc[12]) && check(13) === Number(doc[13])
}

/** O tipo sai do tamanho do número já normalizado: 14 caracteres é CNPJ, 11 é CPF. */
export function docTypeOf(docNumber: string): DocType {
  return normalizeDocNumber(docNumber).length === 14 ? 'cnpj' : 'cpf'
}

/**
 * Documento de clínica com a máscara do tipo: "11.222.333/0001-81" · "123.456.789-09". Sem o tipo
 * (durante a digitação do campo "CNPJ ou CPF"), até 11 dígitos é CPF e dali em diante é CNPJ.
 */
export function formatDocNumber(docNumber: string, docType?: DocType) {
  const doc = normalizeDocNumber(docNumber)
  const isCPF = docType ? docType === 'cpf' : /^\d{0,11}$/.test(doc)
  return isCPF ? formatCPF(doc) : formatCNPJ(doc)
}
